import { AnimatePresence, motion } from "framer-motion";
import { Baby, Blocks, Bus, Moon, Sun, ChevronDown } from "lucide-react";
import { useExperience } from "@/context/ExperienceContext";
import { CtaButton } from "./CtaButton";

const ICONS = { bus: Bus, baby: Baby, blocks: Blocks, sun: Sun, moon: Moon };
const ease = [0.22, 1, 0.36, 1];

function ServiceCard({ card, sceneId }) {
  const Icon = ICONS[card.icon];
  return (
    <motion.aside
      key={`card-${sceneId}`}
      data-testid={`service-card-${sceneId}`}
      className={`rrb-card pos-${card.pos}`}
      initial={{ opacity: 0, y: 18, rotate: -1.5 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.6, ease, delay: 0.15 }}
    >
      <span className={`rrb-card-icon icon-${card.icon}`}><Icon className="h-5 w-5" strokeWidth={2.4} /></span>
      <div>
        <h3 className="font-display text-lg font-semibold leading-tight">{card.title}</h3>
        <p className="mt-1 text-sm leading-snug opacity-80">{card.body}</p>
        {card.note && <span className="rrb-soon" data-testid={`service-card-note-${sceneId}`}>{card.note}</span>}
      </div>
    </motion.aside>
  );
}

// Mobile: the service card folds into the headline panel so nothing floats over faces.
function MobileCardChip({ card, sceneId }) {
  const Icon = ICONS[card.icon];
  return (
    <div data-testid={`service-card-${sceneId}`} className="rrb-card-chip">
      <span className={`rrb-card-icon icon-${card.icon}`}><Icon className="h-3.5 w-3.5" strokeWidth={2.6} /></span>
      <span className="font-display font-semibold">{card.title}</span>
      {card.note && <span className="rrb-soon !mt-0" data-testid={`service-card-note-${sceneId}`}>{card.note}</span>}
    </div>
  );
}

export function SceneOverlay({ textOn, mobile }) {
  const { scene, finale } = useExperience();
  const showCta = scene.cta && !(mobile && scene.cta === "eligibility");
  const primary = scene.cta === "eligibility";
  return (
    <div className="rrb-overlay" data-testid="scene-overlay" style={{ opacity: 1 - Math.min(1, finale * 3) }}>
      <AnimatePresence mode="wait">
        {textOn && (
          <motion.div
            key={scene.id}
            data-testid={`scene-copy-${scene.id}`}
            className={`rrb-copy pos-${scene.textPos}`}
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.55, ease }}
          >
            <p className="rrb-eyebrow">{scene.eyebrow}</p>
            <h1 data-testid="scene-headline" className="rrb-headline">{scene.headline}</h1>
            {mobile && scene.card && <MobileCardChip card={scene.card} sceneId={scene.id} />}
            {showCta && (
              <div className="mt-3">
                <CtaButton kind={scene.cta} size={primary ? "md" : "sm"} tone={primary ? "primary" : "soft"} testId={`scene-cta-${scene.id}`} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {textOn && scene.card && !mobile && <ServiceCard card={scene.card} sceneId={scene.id} />}
      </AnimatePresence>
    </div>
  );
}

export function ScrollHint({ mobile, onStart }) {
  return (
    <button type="button" className="rrb-hint" data-testid="scroll-hint" onClick={onStart}>
      <span>{mobile ? "Swipe up to begin" : "Scroll to begin"}</span>
      <ChevronDown className="h-4 w-4" />
    </button>
  );
}
